import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Typography,
} from '@mui/material'
import { NavLink, useLocation } from 'react-router-dom'
import { useLang } from '@/i18n/useLang'
import { buildLangHref, stripLangPrefix } from '@data/nav'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useAuth } from '@components/AuthContext'

export default function AppSidebar({ items = [] }) {
  const location = useLocation()
  const lang = useLang()

  const clearSessionOnNavigate = () => {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('setpage')) {
        sessionStorage.setItem(key, '1')
      }
    })
  }

  const currentPath = stripLangPrefix(location.pathname)

  const { user } = useAuth()
  const clss = user?.clss ?? ''

  const isProfGroup =
    clss === 'PROFESSOR' ||
    clss === 'ASSISTANT'

  const filteredItems = useMemo(() => {
    const filterRecursive = (arr) =>
      arr
        .map(item => {
          if (!isProfGroup && item.professorOnly) return null
          if (isProfGroup && item.noProfessor) return null
          const children = item.children ? filterRecursive(item.children) : []
          return { ...item, children }
        })
        .filter(Boolean)

    const result = filterRecursive(items)

    if (items.__rootLabel) {
      result.__rootLabel = items.__rootLabel
    }

    return result
  }, [items, isProfGroup])

  const title = useMemo(() => filteredItems.__rootLabel || '', [filteredItems])
  const level2 = useMemo(
    () => filteredItems.filter(it => !it?.subMain && !it?.hidden),
    [filteredItems]
  )

  const [openKey, setOpenKey] = useState(null)

  useEffect(() => {
    let matchedKey = null

    for (const l2 of level2) {
      const key = `${l2.label}_${l2.href || ''}`
      const has3 = Array.isArray(l2.children) && l2.children.length > 0
      const hitParent =
        l2.href &&
        (currentPath === l2.href || currentPath.startsWith(`${l2.href}/`))
      const hitChild =
        has3 && l2.children.some(c => c.href && currentPath.startsWith(c.href))

      if (hitParent || hitChild) {
        matchedKey = key
        break
      }
    }

    setOpenKey(matchedKey)
  }, [currentPath, level2])

  const toggle = (key) => {
    setOpenKey(prev => (prev === key ? null : key))
  }

  return (
    <Box
      component="nav"
      sx={{
        width: '240px',
        overflowY: 'auto',
        pt: 7,
        position: 'sticky',
        top: '184px',
      }}
    >
      {/* 1Depth : 제목 영역 */}
      {title && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ pl: 1, height: '36px' }}>
            {title}
          </Typography>
        </Box>
      )}
      <List disablePadding>
        {level2.map((l2) => {
          const key = `${l2.label}_${l2.href || ''}`
          const children3 = (l2.children || []).filter(c => !c?.hidden)
          const has3 = children3.length > 0
          const isOpen = openKey === key

          const isActive = (() => {
            const current = currentPath.replace(/\/+$/, '')
            const matchByHref =
              (l2.href && current.startsWith(l2.href.replace(/\/+$/, ''))) ||
              (l2.children || []).some(c =>
                current.startsWith((c.href || '').replace(/\/+$/, ''))
              )

            const matchByHiddenLabel = filteredItems.some(
              h =>
                h.hidden &&
                h.label === l2.label &&
                current.startsWith(h.href?.replace(/\/+$/, ''))
            )

            return matchByHref || matchByHiddenLabel || isOpen
          })()

          return (
            <ListItem
              key={key}
              disablePadding
              sx={{ display: 'block' }}
            >
              {/* 2Depth */}
              <ListItemButton
                onClick={() => {
                  if (!has3) {
                    clearSessionOnNavigate()
                  } else {
                    toggle(key)
                  }
                }}
                component={!has3 ? NavLink : 'button'}
                to={!has3 ? buildLangHref(l2.href, lang) : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '56px',
                  bgcolor: isActive ? 'primary.50' : 'transparent',
                  px: 2,
                  borderRadius: 2,
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: isActive
                      ? (theme) => `${theme.palette.primary[50]} !important`
                      : 'transparent !important',
                    '& .MuiListItemText-primary': {
                      color: 'primary.dark',
                    },
                  },
                }}
              >
                <ListItemText
                  primary={l2.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? '600' : '400',
                    color: isActive ? 'primary.dark' : 'text.primary',
                    sx: { transition: 'all 0.2s' },
                  }}
                  sx={{ my: 0 }}
                />

                {has3 &&
                  (isOpen ? (
                    <KeyboardArrowUpIcon
                      className="arrow-icon"
                      sx={{
                        fontSize: '24px',
                        transition: 'color 0.2s ease',
                        color: isActive ? 'primary.dark' : 'text.primary',
                      }}
                    />
                  ) : (
                    <KeyboardArrowDownIcon
                      className="arrow-icon"
                      sx={{
                        fontSize: '24px',
                        transition: 'color 0.2s ease',
                        color: isActive ? 'primary.dark' : 'text.primary',
                      }}
                    />
                  ))}
              </ListItemButton>

              {/* 3Depth */}
              {has3 && (
                <>
                  <Collapse
                    in={isOpen}
                    timeout="auto"
                    unmountOnExit
                    sx={{
                      transition: theme =>
                        theme.transitions.create(
                          ['height', 'opacity', 'transform'],
                          {
                            duration: 400,
                            easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
                          }
                        ),
                      opacity: isOpen ? 1 : 0,
                      transform: isOpen ? 'translateY(0)' : 'translateY(-4px)',
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: 'grey.50',
                        p: 2,
                        mb: 2,
                        borderRadius: 1,
                      }}
                    >
                      <List disablePadding sx={{ pb: 0 }}>
                        {children3.map((l3, j) => {
                          const normalize = (p) =>
                            (p || '').replace(/\/+$/, '')
                          const cur = normalize(currentPath)
                          const href = normalize(l3.href)
                          const isChildActive =
                            cur === href || cur.startsWith(href + '/')

                          return (
                            <ListItem
                              disablePadding
                              key={`${l3.label}_${j}`}
                            >
                              <ListItemButton
                                component={NavLink}
                                to={buildLangHref(l3.href, lang)}
                                onClick={clearSessionOnNavigate}
                                sx={{
                                  px: 0,
                                  py: 0,
                                  my: 0.75,
                                  backgroundColor: 'transparent',
                                  '&:hover': {
                                    backgroundColor: 'transparent !important',
                                    '& .MuiTypography-root': {
                                      color: 'primary.dark',
                                    },
                                  },
                                  '&::before': { display: 'none' },
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Typography
                                      fontWeight={isChildActive ? 700 : 400}
                                      color={
                                        isChildActive
                                          ? 'primary.dark'
                                          : 'text.primary'
                                      }
                                      sx={{
                                        transition: 'color 0.2s ease',
                                      }}
                                    >
                                      - {l3.label}
                                    </Typography>
                                  }
                                  sx={{ my: 0 }}
                                />
                              </ListItemButton>
                            </ListItem>
                          )
                        })}
                      </List>
                    </Box>
                  </Collapse>

                  <Box
                    sx={{
                      borderBottom: !isOpen ? 'none' : '1px solid',
                      borderColor: 'grey.200',
                    }}
                  />
                </>
              )}
            </ListItem>
          )
        })}
      </List>
    </Box>
  )
}
